import moment from "moment";
import { CONFIG, ZONE_ORDER } from "./CONFIG";

/**
 * Severity × momentum scoring for the topic-clustering tree. Pure: takes the
 * enriched tree (already narrowed by dataset / voice / signal, but NOT by
 * period — momentum needs the whole time series), the selected period and
 * the signal metadata, and returns scored findings (or buckets) with every
 * intermediate term kept so the UI can explain each number.
 *
 * SEVERITY — a weight, never volume in disguise:
 *   severity = signalWeight × reach × voice
 *     signalWeight  per-signal (BASE_TAXONOMY severityWeight, via signalMetaById),
 *                   else the category default, else DEFAULT_SEVERITY_WEIGHT
 *     reach         1 + ln(distinct documents) — breadth, log-damped
 *     voice         VOICE_PENALTY when most classified observations are
 *                   product-side speech, else 1
 *   Buckets roll up by MAX over their findings, never mean.
 *
 * MOMENTUM — signed, share-based, confidence-damped. Two modes, picked per
 * finding by how much history exists at the period's grain:
 *   history  ≥ MIN_HISTORY_PERIODS earlier periods with data → the finding's
 *            share this period vs an EWMA baseline of its own past shares,
 *            in units of its own EWMA standard deviation (a z-score). The
 *            deviation is floored RELATIVE to the baseline (SIGMA_FLOOR_REL).
 *   delta    otherwise → share this period minus share in the comparison
 *            period, RELATIVE to that share (DELTA_BAND_REL), not in
 *            percentage points: a sub-topic's absolute share shrinks as the
 *            taxonomy grows, so points flattened every big tenant to "steady".
 *            The comparison period is the FIRST period with data in the
 *            trend window (`window` periods including the selected one), so
 *            window 2 = the previous period, window 3 = the one before that.
 *            The band is per sub-topic: DELTA_BAND_REL or, if larger, the
 *            move chance alone would produce for a share this thin
 *            (DELTA_NOISE_SIGMAS × the sampling noise of the relative
 *            change) — so 7 → 12 reads steady while 44 → 324 reads rising,
 *            and the index means the same thing it means in history mode:
 *            the move in units of what this sub-topic does by chance.
 *   Both scales are held up by the share CONFIDENCE_FLOOR observations take
 *   of the corpus (minShareOf), so hair-thin shares can't amplify noise.
 *   Both are multiplied by confidence = min(1, max(n_now, n_normal) /
 *   CONFIDENCE_FLOOR) — see confidenceOf for why the larger side counts —
 *   and both are expressed as an INDEX where the steady band is ±1, so the
 *   field and the zones read the same regardless of mode.
 */

const GRAIN_FORMAT = { month: "YYYY-MM", week: "GGGG-[W]WW", year: "YYYY" };
const GRAIN_UNIT = { month: "month", week: "isoWeek", year: "year" };
const GRAIN_STEP = { month: "month", week: "week", year: "year" };

// Same period-value → grain reading as previousWindowFor (LeadingCategoryTrend).
export const periodGrain = (period) => {
  const v = period?.value || "";
  if (/^month_|_month$/.test(v)) return "month";
  if (/^year_|_year$/.test(v)) return "year";
  if (/_week$/.test(v)) return "week";
  return null;
};

const keyOf = (iso, grain) => moment(iso).format(GRAIN_FORMAT[grain]);

// A period option's key at its own grain ("2026-08" for August) — what the
// scorer calls it in `compareKey`, `nowKey` and the per-period series.
export const periodKey = (period) => {
  const grain = periodGrain(period);
  return grain && period?.from ? keyOf(period.from, grain) : null;
};

// The key of the period immediately BEFORE the given one, at its own grain
// (the month before a month, the week before a week) — whether or not it
// holds any observations. A caller that wants "compare with the previous
// period, plainly" passes this as scoreTree's `compareKey`.
export const previousPeriodKey = (period) => {
  const grain = periodGrain(period);
  if (!grain || !period?.from) return null;
  return moment(period.from).startOf(GRAIN_UNIT[grain]).subtract(1, GRAIN_STEP[grain]).format(GRAIN_FORMAT[grain]);
};

export const keyLabel = (key, grain) => {
  if (grain === "month") return moment(key, "YYYY-MM").format("MMM 'YY");
  if (grain === "week") return key.replace(/^(\d{4})-W(\d{2})$/, "wk $2 '$1").replace("'20", "'");
  return key;
};

// The observations a node "contains": findings own theirs; buckets own their
// findings' plus their parked ones; legacy flat nodes are their own row.
export const rowsOf = (bucket) => (bucket.children?.length ? bucket.children : [bucket]);
const bucketObservations = (bucket) => [
  ...rowsOf(bucket).flatMap((r) => r.observations || []),
  ...(bucket.parkedObservations || []),
];
const allObservationsOf = (tree) => [
  ...(tree.tree || []).flatMap(bucketObservations),
  ...(tree.unplacedObservations || []),
];

const countByKey = (observations, grain) => {
  const counts = {};
  for (const o of observations) {
    if (!o.dateThatMatters) continue;
    // Pre-keyed observations (Topics V2 unrolls server series that already
    // carry the period key) skip the per-observation date formatting, which
    // is what makes re-scoring 70k observations lag.
    const k = o.periodKey || keyOf(o.dateThatMatters, grain);
    counts[k] = (counts[k] || 0) + 1;
  }
  return counts;
};

// ── Node stats ────────────────────────────────────────────────────────
//
// Everything the maths below needs from a node: four numbers, its dataset
// list and its counts per period. A node can arrive either way —
//
//   `observations`  the Topics tab's tree, which really holds its annotations;
//   `stats`         Topics V2, whose counts endpoint already sends these
//                   aggregates (annotationCount, docs, voice, datasets) plus
//                   a per-period series, so nothing has to be unrolled.
//
// Only this adapter knows the difference. Note what the reductions are: a
// distinct-document count, two voice tallies, a dataset set and counts per
// period — which is exactly what the server sends, and why the aggregate
// path needs no observations at all.
export const statsOfObservations = (observations = [], grain, fallbackDatedDatasets) => {
  const docIds = new Set();
  const datasets = new Set();
  const countsByKey = {};
  const countsByDataset = {};
  let classified = 0;
  let customer = 0;
  let fallbackDatedN = 0;
  for (const o of observations) {
    if (o.documentId) docIds.add(o.documentId);
    if (o.dataset) {
      datasets.add(o.dataset);
      if (fallbackDatedDatasets?.has(o.dataset)) fallbackDatedN += 1;
    }
    if (typeof o.customerVoice === "boolean") {
      classified += 1;
      if (o.customerVoice) customer += 1;
    }
    if (!grain || !o.dateThatMatters) continue;
    // Pre-keyed observations skip the per-observation date formatting.
    const k = o.periodKey || keyOf(o.dateThatMatters, grain);
    countsByKey[k] = (countsByKey[k] || 0) + 1;
    if (o.dataset) {
      const perDataset = (countsByDataset[o.dataset] ||= {});
      perDataset[k] = (perDataset[k] || 0) + 1;
    }
  }
  return {
    n: observations.length,
    docs: docIds.size,
    classified,
    customer,
    datasets: [...datasets],
    countsByKey,
    // Per-dataset series. null means "not knowable here" (the aggregate
    // path), which turns the dataset-level readings off rather than faking
    // them — see risingInOf.
    countsByDataset,
    fallbackDatedN,
  };
};

// A node's stats, whichever way it arrived.
const statsOf = (node, grain, fallbackDatedDatasets) =>
  node?.stats || statsOfObservations(node?.observations || [], grain, fallbackDatedDatasets);

// Counts per period add across nodes, so a tree total is the sum of its
// parts — the same number that counting every observation once would give.
const addCounts = (into, counts) => {
  for (const [k, n] of Object.entries(counts || {})) into[k] = (into[k] || 0) + n;
  return into;
};

// Earlier periods of this grain, oldest first, ending just before the period.
const historyKeysFor = (period, grain) => {
  const start = moment(period.from).startOf(GRAIN_UNIT[grain]);
  const keys = [];
  for (let i = CONFIG.HISTORY_MAX_PERIODS; i >= 1; i -= 1) {
    keys.push(start.clone().subtract(i, GRAIN_STEP[grain]).format(GRAIN_FORMAT[grain]));
  }
  return keys;
};

// Exponentially weighted mean + standard deviation over a series, oldest
// first. Standard recursive form: the last point carries weight alpha, the
// one before alpha(1−alpha), and so on.
const ewma = (values, alpha) => {
  let mean = values[0];
  let variance = 0;
  for (let i = 1; i < values.length; i += 1) {
    const diff = values[i] - mean;
    const incr = alpha * diff;
    mean += incr;
    variance = (1 - alpha) * (variance + alpha * diff * diff);
  }
  return { mean, sigma: Math.sqrt(variance) };
};

// The period a delta is measured from: the first period with data in the
// trend window, or the earliest there is when the window outruns the data.
const comparisonPeriod = (withData, window) =>
  withData.length ? withData[Math.max(0, withData.length - (window - 1))] : undefined;

// "new" (no observations in the comparison period) is a flavour of rising,
// not a bypass of it: the index still has to clear the band, so a thin row
// with a couple of first observations stays steady until it earns more.
const bandOf = (index, isNew) => {
  if (isNew && index > 1) return "new";
  if (index > 1) return "rising";
  if (index < -1) return "cooling";
  return "steady";
};

// ── Severity ──────────────────────────────────────────────────────────

// Per-category semantics: polarity (good / bad news) and the category-level
// severity weight a signal falls back to when it has no weight of its own.
// The tree serves these per tenant on `signalCategories`; this is the PM
// default for callers that pass none.
const PM_CATEGORY_SEMANTICS = {
  userDissatisfaction: { polarity: "negative", severityWeight: 0.7 },
  userNeeds: { polarity: "neutral", severityWeight: 0.4 },
  userBehaviour: { polarity: "neutral", severityWeight: 0.3 },
  userSatisfaction: { polarity: "positive", severityWeight: 0.1 },
};

const semanticsFor = (signalCategories) =>
  signalCategories?.length
    ? Object.fromEntries(
        signalCategories.map((c) => [c.value, { polarity: c.polarity, severityWeight: c.severityWeight }])
      )
    : PM_CATEGORY_SEMANTICS;

const severityFromStats = ({ docs, classified, customer }, signalMeta, categorySemantics = PM_CATEGORY_SEMANTICS) => {
  const categoryWeight = categorySemantics[signalMeta?.category]?.severityWeight;
  const weight =
    typeof signalMeta?.severityWeight === "number"
      ? signalMeta.severityWeight
      : typeof categoryWeight === "number"
      ? categoryWeight
      : CONFIG.DEFAULT_SEVERITY_WEIGHT;
  const reach = 1 + Math.log(Math.max(1, docs));
  const customerShare = classified ? customer / classified : null;
  const voice = classified && customerShare < 0.5 ? CONFIG.VOICE_PENALTY : 1;
  return {
    value: weight * reach * voice,
    weight,
    weightSource:
      typeof signalMeta?.severityWeight === "number"
        ? "signal"
        : typeof categoryWeight === "number"
        ? "category"
        : "default",
    docs,
    reach,
    voice,
    customerShare,
    classified,
    customer,
  };
};

// Severity from an observation array — the Topics tab's entry point, and
// what the tests call.
export const scoreSeverity = ({ observations, stats, signalMeta, categorySemantics = PM_CATEGORY_SEMANTICS }) =>
  severityFromStats(stats || statsOfObservations(observations || []), signalMeta, categorySemantics);

// ── Momentum ──────────────────────────────────────────────────────────

// The damper. Evidence for a move is the LARGER of the two sides being
// compared — what the finding holds now, or what it normally held. Using
// only the current count would give a finding that dropped from eight
// observations to zero a confidence of zero, so it could never read
// "cooling", and cooling is the resolution signal the whole view exists to
// catch. A 1→3 rise is still damped (evidence 3 of 4); an 8→0 drop is not.
const confidenceOf = (nNow, nReference) =>
  Math.min(1, Math.max(nNow, nReference || 0) / CONFIG.CONFIDENCE_FLOOR);

// The share (in points) that CONFIDENCE_FLOOR observations take of a corpus
// of `total` — the thinnest share a relative comparison may be scaled by.
const minShareOf = (total) => (total > 0 ? (CONFIG.CONFIDENCE_FLOOR / total) * 100 : 0);

const scoreMomentum = ({ stats, totals, nowKey, histKeys, window, compareKey }) => {
  const counts = stats.countsByKey;
  const nNow = counts[nowKey] || 0;
  const totalNow = totals[nowKey] || 0;

  if (stats.fallbackDatedN && stats.n && stats.fallbackDatedN / stats.n > CONFIG.MAX_FALLBACK_DATED_SHARE) {
    return { available: false, reason: "import-dated", nNow, totalNow };
  }
  if (!totalNow) return { available: false, reason: "no observations in this period", nNow, totalNow };
  if (totalNow < CONFIG.MIN_NOW_TOTAL) return { available: false, reason: "this period too thin", nNow, totalNow };

  const shareNow = (nNow / totalNow) * 100;
  const history = histKeys.keys
    .filter((k) => totals[k] > 0)
    .map((k) => ({ key: k, n: counts[k] || 0, total: totals[k], share: ((counts[k] || 0) / totals[k]) * 100 }));

  if (history.length >= CONFIG.MIN_HISTORY_PERIODS) {
    const { mean: baseline, sigma } = ewma(history.map((h) => h.share), CONFIG.EWMA_ALPHA);
    // The finding's normal COUNT, same weighting — the reference side of the
    // evidence for the damper.
    const { mean: nBaseline } = ewma(history.map((h) => h.n), CONFIG.EWMA_ALPHA);
    const confidence = confidenceOf(nNow, nBaseline);
    const meanTotal = history.reduce((acc, h) => acc + h.total, 0) / history.length;
    const sigmaEff = Math.max(sigma, CONFIG.SIGMA_FLOOR_REL * Math.max(baseline, minShareOf(meanTotal)));
    const velocity = (shareNow - baseline) / sigmaEff;
    const momentum = velocity * confidence;
    const index = momentum / CONFIG.Z_BAND;
    const isNew = history.every((h) => h.n === 0) && nNow >= CONFIG.CONFIDENCE_FLOOR;
    return {
      available: true,
      mode: "history",
      nNow,
      nBaseline,
      totalNow,
      shareNow,
      baseline,
      sigma,
      sigmaEff,
      velocity,
      confidence,
      momentum,
      index,
      band: bandOf(index, isNew),
      history,
    };
  }

  // The comparison period: the one named (`compareKey` — absent from
  // `history` when it holds nothing, which reads as no previous period), or
  // the trend window's first period with data.
  const prev = compareKey !== undefined ? history.find((h) => h.key === compareKey) : comparisonPeriod(history, window);
  if (!prev) return { available: false, reason: "no previous period", nNow, totalNow, shareNow, history };
  if (prev.total < CONFIG.MIN_PRIOR_TOTAL) {
    return { available: false, reason: "previous period too thin", nNow, totalNow, shareNow, history, prev };
  }
  const confidence = confidenceOf(nNow, prev.n);
  const delta = shareNow - prev.share; // points
  // The move as a fraction of the comparison share (floored, see minShareOf).
  const prevShare = Math.max(prev.share, minShareOf(prev.total));
  const relDelta = delta / prevShare;
  // How far a share this size lands from itself by chance, as a fraction of
  // itself, when drawn twice (once per period): √2 · √((1−p) / (p·N)). For a
  // thin share that is ≈ √2 / √count — 100 observations wobble ~14%, 4 ~70%.
  const p = prevShare / 100;
  const noise = Math.SQRT2 * Math.sqrt((1 - p) / (p * prev.total));
  const threshold = Math.max(CONFIG.DELTA_BAND_REL, CONFIG.DELTA_NOISE_SIGMAS * noise);
  const momentum = relDelta * confidence;
  const index = momentum / threshold;
  const isNew = prev.n === 0 && nNow >= CONFIG.CONFIDENCE_FLOOR;
  return {
    available: true,
    mode: "delta",
    nNow,
    totalNow,
    shareNow,
    prev,
    delta,
    relDelta,
    noise,
    threshold,
    confidence,
    momentum,
    index,
    band: bandOf(index, isNew),
    history,
  };
};

// Observation totals per period, per dataset — the denominator of a
// dataset's share of a sub-topic, so "rising in" is a rate test (the image's
// "districts rising"), not a volume artefact.
const datasetTotalsFrom = (allStats) => {
  const out = {};
  for (const stats of allStats) {
    // One node with no per-dataset series (the aggregate path) makes the
    // whole denominator unknowable — say so rather than under-count.
    if (!stats.countsByDataset) return null;
    for (const [ds, counts] of Object.entries(stats.countsByDataset)) addCounts((out[ds] ||= {}), counts);
  }
  return out;
};

// How many of a sub-topic's datasets saw its share grow between the
// comparison period and now. Null when there is nothing to compare.
const risingInOf = ({ stats, datasetTotals, nowKey, compareKey }) => {
  if (!compareKey || !nowKey || !datasetTotals || !stats.countsByDataset) return null;
  const { datasets } = stats;
  const risingDatasets = new Set();
  for (const ds of datasets) {
    const counts = stats.countsByDataset[ds] || {};
    const totalNow = datasetTotals[ds]?.[nowKey] || 0;
    const totalThen = datasetTotals[ds]?.[compareKey] || 0;
    const shareNow = totalNow ? (counts[nowKey] || 0) / totalNow : 0;
    const shareThen = totalThen ? (counts[compareKey] || 0) / totalThen : 0;
    if (shareNow > shareThen) risingDatasets.add(ds);
  }
  return { rising: risingDatasets.size, total: datasets.length, risingDatasets };
};

// ── Zones ─────────────────────────────────────────────────────────────

export const zoneOf = ({ severity, momentum }) => {
  if (!momentum?.available) return null;
  const high = severity.value >= CONFIG.SEVERITY_HIGH;
  const band = momentum.band === "new" ? "rising" : momentum.band;
  if (band === "rising") return high ? "fires" : "emerging";
  if (band === "cooling") return high ? "resolving" : "fading";
  return high ? "chronic" : "background";
};

const zoneRank = (zone) => (zone ? ZONE_ORDER.indexOf(zone) : ZONE_ORDER.length);

// Observation totals per period key across a whole tree — what the layout's
// entry jump uses to skip months too thin to score (see CONFIG.MIN_NOW_TOTAL).
export const observationTotalsByPeriod = (tree, grain = "month") =>
  countByKey(allObservationsOf(tree || {}), grain);

// ── Entry point ───────────────────────────────────────────────────────

/**
 * @param {object} args
 * @param {object} args.historyTree   enriched tree, period-UNfiltered
 * @param {object} args.period        selected period option ({value, from, to})
 * @param {object} args.signalMetaById
 * @param {Set<string>} [args.fallbackDatedDatasets] content types whose
 *        "date that matters" is the annotation's createdAt
 * @param {"findings"|"buckets"} [args.grain="findings"]
 * @param {number} [args.window=CONFIG.TREND_WINDOW] periods with data the
 *        trend spans, the selected one included (see CONFIG.TREND_WINDOW)
 */
export const scoreTree = ({
  historyTree,
  period,
  signalMetaById = {},
  signalCategories = null,
  fallbackDatedDatasets,
  grain = "findings",
  window = CONFIG.TREND_WINDOW,
  // The period to compare with, by key (see previousPeriodKey). Given, it
  // replaces the trend window's choice everywhere a comparison is made: the
  // share changes, the per-period columns, and delta-mode momentum.
  // History-mode momentum reads the finding's own normal either way, exactly
  // as it ignores `window`. Left undefined, `window` decides as before.
  compareKey: explicitCompareKey,
}) => {
  const categorySemantics = semanticsFor(signalCategories);
  const timeGrain = periodGrain(period);
  const meta = {
    timeGrain,
    nowKey: null,
    nowLabel: period?.display || "",
    prevLabel: null,
    totalNow: 0,
    historyPeriods: 0,
    window,
    // The period keys the trend spans, oldest first, the selected one last;
    // compareKey is the first of them — what a delta is measured from.
    windowKeys: [],
    compareKey: null,
    // Observation totals per period key across the whole scope — the
    // denominator behind every share the list prints.
    totalsByKey: {},
    mode: null,
    momentumAvailable: !!(timeGrain && period?.from),
    reason: !period ? "no period" : !timeGrain ? "no comparison window for this period" : null,
  };

  // Every node's stats, once. The leaves are a bucket's rows plus its
  // parked pseudo-node, and the tree's unplaced row; totals and the
  // per-dataset denominators are sums over exactly those.
  const statsCache = new Map();
  const statsFor = (node) => {
    let cached = statsCache.get(node);
    if (!cached) {
      cached = statsOf(node, timeGrain, fallbackDatedDatasets);
      statsCache.set(node, cached);
    }
    return cached;
  };
  const foldObservations = (observations) => statsOfObservations(observations, timeGrain, fallbackDatedDatasets);
  // A bucket scored as a node. The aggregate path sends the topic's own
  // figures (distinct documents across the whole topic, which is NOT the sum
  // of its rows'); otherwise fold the observations it contains.
  // `bucketStats` deliberately, NOT `stats`: a bucket's own figures cover
  // its rows AND its parked row, so if it were read as a row's stats (the
  // rowsOf fallback for a childless bucket) the parked counts would land in
  // the tree totals twice.
  const bucketStatsOf = (bucket) => bucket.bucketStats || foldObservations(bucketObservations(bucket));

  const leafStats = [];
  for (const bucket of historyTree?.tree || []) {
    for (const row of rowsOf(bucket)) leafStats.push(statsFor(row));
    leafStats.push(bucket.parkedStats || foldObservations(bucket.parkedObservations || []));
  }
  leafStats.push(historyTree?.unplacedStats || foldObservations(historyTree?.unplacedObservations || []));

  const totals = timeGrain ? leafStats.reduce((acc, st) => addCounts(acc, st.countsByKey), {}) : {};
  const datasetTotals = timeGrain ? datasetTotalsFrom(leafStats) : null;
  const histKeys = timeGrain && period?.from ? { grain: timeGrain, keys: historyKeysFor(period, timeGrain) } : null;
  if (histKeys) {
    meta.totalsByKey = totals;
    meta.nowKey = keyOf(period.from, timeGrain);
    meta.totalNow = totals[meta.nowKey] || 0;
    if (meta.totalNow < CONFIG.MIN_NOW_TOTAL) {
      meta.momentumAvailable = false;
      meta.reason = "this period too thin";
    }
    const withData = histKeys.keys.filter((k) => totals[k] > 0);
    meta.historyPeriods = withData.length;
    meta.mode = withData.length >= CONFIG.MIN_HISTORY_PERIODS ? "history" : "delta";
    const compareKey = explicitCompareKey !== undefined ? explicitCompareKey : comparisonPeriod(withData, window);
    meta.compareKey = compareKey || null;
    // The periods the trend spans, oldest first. From the window: the
    // periods with data from the comparison one on. From a named period:
    // every calendar period from it on, empty ones included — the reader
    // asked for that span.
    meta.windowKeys =
      explicitCompareKey !== undefined
        ? [...(compareKey ? histKeys.keys.slice(Math.max(0, histKeys.keys.indexOf(compareKey))) : []), meta.nowKey]
        : [...withData.slice(compareKey ? withData.indexOf(compareKey) : withData.length), meta.nowKey];
    meta.prevLabel = compareKey ? keyLabel(compareKey, timeGrain) : null;
    meta.nowLabel = keyLabel(meta.nowKey, timeGrain);
  }

  const scoreNode = ({ row, stats, bucket, severity }) => {
    const signalMeta = row.signalId ? signalMetaById[String(row.signalId)] : null;
    const sev = severity || severityFromStats(stats, signalMeta, categorySemantics);
    const momentum = histKeys
      ? scoreMomentum({ stats, totals, nowKey: meta.nowKey, histKeys, window, compareKey: explicitCompareKey })
      : { available: false, reason: meta.reason, nNow: 0, totalNow: 0 };
    // A finding under a good-news category is a win, not a priority.
    const isWin = categorySemantics[signalMeta?.category]?.polarity === "positive";
    const datasets = stats.datasets;
    // What the list's period columns print — kept even when momentum is
    // unavailable, so a row with no score still shows its counts.
    const periodCounts = histKeys ? stats.countsByKey : {};
    const risingIn =
      histKeys && momentum.available
        ? risingInOf({ stats, datasetTotals, nowKey: meta.nowKey, compareKey: meta.compareKey })
        : null;
    return {
      nodeId: String(row.nodeId),
      row,
      label: row.label,
      description: row.description,
      bucketLabel: bucket?.label || null,
      signal: signalMeta,
      category: signalMeta?.category || null,
      n: stats.n,
      datasets,
      severity: sev,
      momentum,
      periodCounts,
      risingIn,
      isWin,
      zone: isWin ? null : zoneOf({ severity: sev, momentum }),
    };
  };

  const items = [];
  for (const bucket of historyTree?.tree || []) {
    if (grain === "buckets") {
      const findings = rowsOf(bucket).map((row) => ({
        row,
        severity: severityFromStats(statsFor(row), row.signalId ? signalMetaById[String(row.signalId)] : null, categorySemantics),
      }));
      // Worst finding sets the bucket's severity; its terms travel along so
      // the explainer can name which finding it was.
      const worst = findings.reduce((a, b) => (!a || b.severity.value > a.severity.value ? b : a), null);
      if (!worst) continue;
      items.push(
        scoreNode({
          row: { ...bucket, signalId: worst.row.signalId },
          stats: bucketStatsOf(bucket),
          bucket: null,
          severity: { ...worst.severity, worstFinding: worst.row.label },
        })
      );
    } else {
      for (const row of rowsOf(bucket)) {
        const rowStats = statsFor(row);
        if (!rowStats.n) continue;
        items.push(scoreNode({ row, stats: rowStats, bucket: bucket === row ? null : bucket }));
      }
    }
  }

  const byPriority = (a, b) =>
    zoneRank(a.zone) - zoneRank(b.zone) ||
    b.severity.value - a.severity.value ||
    (b.momentum.index || 0) - (a.momentum.index || 0);
  const byMomentum = (a, b) =>
    (b.momentum.available ? 1 : 0) - (a.momentum.available ? 1 : 0) ||
    (b.momentum.index || 0) - (a.momentum.index || 0) ||
    b.n - a.n;

  const priorities = items.filter((i) => !i.isWin).sort(byPriority);
  const wins = items.filter((i) => i.isWin).sort(byMomentum);
  return { priorities, wins, meta };
};
